// Editor for the project's library of named color schemes. Each scheme
// can be applied to a SECTION via the right-panel "Background scheme"
// picker (see PropertyPanel SectionPropertiesEditor). It is intentionally
// not a place to set page-wide colors — those live on `page.theme.palette`,
// edited in the right panel's Theme tab.

import { Plus, Trash2 } from 'lucide-react'

import { ColorPickerField } from './ColorPickerField'
import { DEFAULT_COLOR_SCHEMES } from './tokens'
import type { ColorScheme, DesignTokens } from './types'

interface Props {
  tokens: DesignTokens
  onChange: (next: DesignTokens) => void
}

let _seq = 0
const idFor = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${++_seq}`

export function TokensPanel({ tokens, onChange }: Props) {
  const set = (patch: Partial<DesignTokens>) => onChange({ ...tokens, ...patch })

  const list = tokens.colorSchemes ?? DEFAULT_COLOR_SCHEMES
  const update = (idx: number, patch: Partial<ColorScheme>) => {
    const next = [...list]; next[idx] = { ...list[idx], ...patch }
    set({ colorSchemes: next })
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.55 }}>
        Schemes here appear in each section's <strong>Background scheme</strong> picker.
        They do not change page-wide colors — those live in the right panel's <strong>Theme</strong> tab.
        Schemes marked <em>From the theme</em> belong to the page's project theme and change with it.
      </p>
      <button
        type="button"
        onClick={() => set({
          colorSchemes: [
            ...list,
            { ...DEFAULT_COLOR_SCHEMES[0], id: idFor('cs'), name: 'New scheme' },
          ],
        })}
        style={addBtnStyle}
      >
        <Plus size={12} /> Add color scheme
      </button>
      {list.map((s, idx) => s.fromTheme ? (
        // A band look of the page's PROJECT theme — rewritten from the theme
        // on every save, so it is edited on the theme (Theme tab), not here.
        <div key={s.id} style={{ ...cardStyle, gap: 6 }}>
          <header style={cardHeader}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>From the theme</span>
          </header>
          <div style={{ display: 'flex', gap: 4 }}>
            {[s.background, s.text, s.textSecondary, s.accent, s.link].filter(Boolean).map((c, i) => (
              <span key={i} title={c} style={{ width: 16, height: 16, borderRadius: 4, background: c, border: '1px solid rgba(0,0,0,0.12)' }} />
            ))}
          </div>
        </div>
      ) : (
        <div key={s.id} style={cardStyle}>
          <header style={cardHeader}>
            <input
              type="text"
              value={s.name}
              onChange={e => update(idx, { name: e.target.value })}
              style={{ ...inputStyle, fontWeight: 500 }}
            />
            <button
              type="button"
              onClick={() => set({ colorSchemes: list.filter(x => x.id !== s.id) })}
              disabled={list.length <= 1}
              style={iconBtn}
              title="Remove scheme"
            >
              <Trash2 size={12} />
            </button>
          </header>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <ColorPickerField label="Background"     value={s.background}    onChange={v => update(idx, { background: v })} />
            <ColorPickerField label="Surface"        value={s.surface}       onChange={v => update(idx, { surface: v })} />
            <ColorPickerField label="Text"           value={s.text}          onChange={v => update(idx, { text: v })} />
            <ColorPickerField label="Text secondary" value={s.textSecondary} onChange={v => update(idx, { textSecondary: v })} />
            <ColorPickerField label="Primary"        value={s.primary}       onChange={v => update(idx, { primary: v })} />
            <ColorPickerField label="On primary"     value={s.onPrimary}     onChange={v => update(idx, { onPrimary: v })} />
            <ColorPickerField label="Accent"         value={s.accent}        onChange={v => update(idx, { accent: v })} />
            <ColorPickerField label="Border"         value={s.border}        onChange={v => update(idx, { border: v })} />
          </div>
        </div>
      ))}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 6, padding: 10,
  display: 'grid', gap: 8, background: 'var(--color-bg-secondary)',
}
const cardHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center',
}
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-secondary)', padding: 4,
}
const addBtnStyle: React.CSSProperties = {
  alignSelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg-secondary)', cursor: 'pointer',
}
